/**
 * Tipos compartidos entre cliente y servidor.
 *
 * Este archivo es la fuente única de verdad para los contratos de datos
 * que viajan por la API REST, los WebSockets (Socket.io) y Supabase.
 * Cualquier cambio aquí debe replicarse o importarse desde ambos proyectos.
 *
 * Convenciones:
 *  - Todo asset visual 3D se modela con `Asset3D` (modelo + LOD + texturas + animaciones).
 *  - Los atletas, estadios y vehículos referencian uno o varios `Asset3D`.
 *  - El render cliente se gobierna con `RenderQuality` y `LightingPreset`.
 */

// ─────────────────────────────────────────────
// Deportes soportados en la plataforma
// ─────────────────────────────────────────────
export type Sport = 'futbol' | 'beisbol' | 'basquetbol';

// ─────────────────────────────────────────────
// ============================================
// MODELOS 3D HIPERREALISTAS (preparación Three.js / R3F)
// ============================================
// ─────────────────────────────────────────────

/** Nivel de detalle del modelo (LOD). */
export type AssetLOD = 'low' | 'medium' | 'high' | 'cinematic';

/** Tipo de textura PBR estándar. */
export type TextureType =
  | 'diffuse'
  | 'normal'
  | 'roughness'
  | 'metallic'
  | 'ao'
  | 'emissive'
  | 'opacity';

/** Resolución de las texturas (potencias de 2). */
export type TextureResolution = '512' | '1k' | '2k' | '4k' | '8k';

/** Entrada de textura para un slot PBR. */
export interface TextureEntry {
  type: TextureType;
  url: string;
  resolution: TextureResolution;
}

/** Clip de animación aplicable a un modelo rigged. */
export interface AnimationClip {
  id: string;
  name: string;
  /** URL al archivo .glb separado o índice dentro del bundle. */
  url: string;
  /** Duración en milisegundos. */
  durationMs: number;
  loop: boolean;
}

/**
 * Asset 3D genérico hiperrealista.
 * Se sirve desde Supabase Storage y se carga en Three.js / R3F.
 */
export interface Asset3D {
  id: string;
  /** Nombre legible del modelo. */
  name: string;
  /** URL al archivo principal (glTF/GLB). */
  modelUrl: string;
  /** Nivel de detalle por defecto. */
  lod: AssetLOD;
  /** Conjunto de texturas PBR. */
  textures: TextureEntry[];
  /** Animaciones (idle, correr, batear, lanzar, celebrar…). */
  animations: AnimationClip[];
  /** Tamaño aproximado comprimido en KB. */
  sizeKB: number;
  /** Formato principal del modelo. */
  format: 'glb' | 'gltf' | 'fbx';
  /** Licencia / atribución (importante para assets descargados). */
  license?: string;
}

/** Calidad de sombras en tiempo real. */
export type ShadowQuality = 'off' | 'low' | 'medium' | 'high' | 'ultra';

/** Momento del día que define la iluminación global del partido. */
export type TimeOfDay = 'day' | 'sunset' | 'night' | 'floodlit' | 'overcast';

/** Condiciones climáticas dentro del estadio. */
export type WeatherCondition = 'clear' | 'rain' | 'snow' | 'fog' | 'storm';

/** Preset de iluminación global para un estadio. */
export interface LightingPreset {
  id: string;
  name: string;
  timeOfDay: TimeOfDay;
  ambientIntensity: number;
  sunIntensity: number;
  shadowQuality: ShadowQuality;
  /** URL al .hdr para IBL (image based lighting) — reflejos realistas. */
  hdrEnvironment?: string;
  /** Color de la luz principal en HEX. */
  keyLightColor: string;
  /** Exposición tonemapping (logarithmic/ACES/Neutral). */
  toneMapping: 'none' | 'linear' | 'reinhard' | 'cineon' | 'aces' | 'agx';
}

/**
 * Calidad de render configurable por el cliente.
 * Se ajusta automáticamente según dispositivo o manualmente en opciones.
 */
export interface RenderQuality {
  preset: 'low' | 'medium' | 'high' | 'ultra' | 'auto';
  antialiasing: boolean;
  shadowQuality: ShadowQuality;
  textureQuality: 'low' | 'medium' | 'high';
  fpsTarget: 30 | 60 | 120;
  /** Habilitar o no bloom (destellos de cámaras / luces estadio). */
  bloom: boolean;
  /** Habilitar desenfoque de movimiento (motion blur). */
  motionBlur: boolean;
}

/**
 * Configuración visual del jugador (atleta hiperrealista).
 * Reemplaza la idea de "muñequito" por un modelo rigged + texturas.
 */
export interface AthleteVisual {
  /** Modelo rigged del atleta (cuerpo + cara). */
  characterModel: Asset3D;
  /** Textura personalizable del uniforme (camiseta). */
  jerseyTexture: TextureEntry;
  /** Equipamiento adicional: casco, guante, balón, bate, zapatillas, etc. */
  equipment: Asset3D[];
  /** Animaciones disponibles (idle, correr, lanzar, celebrar, etc.). */
  animations: AnimationClip[];
  /** Tono de piel (mapa de color o textura). */
  skinTone: string;
}

/** Vehículo (kart, moto, auto) usado en algunos modos (karting, etc.). */
export interface VehicleAsset {
  model: Asset3D;
  type: 'kart' | 'car' | 'bike' | 'boat' | 'custom';
  maxSpeedKmh: number;
  capacity: number;
}

/** Estadio hiperrealista cargable en el cliente. */
export interface StadiumAsset {
  id: string;
  name: string;
  sport: Sport;
  model: Asset3D;
  capacity: number;
  /** Presets de iluminación disponibles para este estadio. */
  lightingPresets: LightingPreset[];
  /** Momento del día seleccionado por defecto. */
  defaultTimeOfDay: TimeOfDay;
  /** Skybox / entorno HDR por defecto. */
  defaultEnvironment?: string;
  /** Tags para mostrar (indoor, outdoor, dome…). */
  tags: string[];
}

// ─────────────────────────────────────────────
// Configuración del avatar (versión extendida)
// ─────────────────────────────────────────────
export interface AvatarConfig {
  /** Color principal (skin, camiseta, etc.) en formato HEX. */
  color: string;
  /** Dorsal (1–99) elegido por el jugador. El servidor asigna uno libre si falta. */
  dorsal?: number;
  /**
   * País que representa (código ISO 3166-1 alfa-2, ver `countries.ts`). Si
   * está, el uniforme usa los colores de su bandera; `null` o ausente = color
   * personalizado (`color`).
   */
  country?: string | null;
  /** Rostro elegido (código de `face.ts`); ausente = uno estable a partir de su id. */
  face?: string | null;
  /** URL pública del avatar 2D por defecto (fallback). */
  avatarUrl: string | null;
  /** Identificador del accesorio equipado (gorra, lentes, etc.). */
  accessory: string | null;
  /** Talla o proporción del personaje. */
  build: 'slim' | 'regular' | 'athletic';
  /** Visual 3D hiperrealista del atleta. */
  athleteVisual: AthleteVisual;
  /** Calidad de render preferida por el cliente. */
  preferredRenderQuality: RenderQuality;
}

// ─────────────────────────────────────────────
// Estadísticas agregadas del jugador
// ─────────────────────────────────────────────
export interface PlayerStats {
  gamesPlayed: number;
  wins: number;
  losses: number;
  draws: number;
  /** Goles, jonrones, canastas, etc. (segun deporte). */
  goals: number;
  /** Puntuación Elo por deporte. */
  elo: Record<Sport, number>;
}

// ─────────────────────────────────────────────
// Jugador / Usuario de la plataforma
// ─────────────────────────────────────────────
export interface Player {
  /** Identificador único (UUID provisto por Supabase Auth). */
  id: string;
  /** Nombre visible único en la plataforma. */
  username: string;
  /** Configuración visual del avatar. */
  avatarConfig: AvatarConfig;
  /** Estadísticas agregadas (se mantienen en Supabase). */
  stats: PlayerStats;
  /** Fecha de creación de la cuenta (ISO 8601). */
  createdAt: string;
  /** Última vez que se conectó (ISO 8601). */
  lastSeen: string | null;
}

// ─────────────────────────────────────────────
// Equipos dentro de un lobby
// ─────────────────────────────────────────────
export type TeamSide = 'home' | 'away' | 'spectator';

// ─────────────────────────────────────────────
// Estado del lobby de espera
// ─────────────────────────────────────────────
export type LobbyStatus = 'waiting' | 'ready' | 'in_game' | 'closed';

// ─────────────────────────────────────────────
// Participante dentro de un lobby
// ─────────────────────────────────────────────
export interface LobbyParticipant {
  player: Player;
  team: TeamSide;
  /** ¿Listo para iniciar la partida? */
  isReady: boolean;
  /** ¿Es el creador de la sala? */
  isHost: boolean;
  /** ID de socket actual (puede cambiar entre sesiones). */
  socketId: string | null;
  joinedAt: string;
}

// ─────────────────────────────────────────────
// Condiciones visuales del partido
// ─────────────────────────────────────────────
export interface MatchConditions {
  timeOfDay: TimeOfDay;
  weather: WeatherCondition;
  /** ID del preset de iluminación del estadio (referencia). */
  lightingPresetId: string | null;
}

// ─────────────────────────────────────────────
// Sala de juego / Lobby
// ─────────────────────────────────────────────
export interface GameRoom {
  /** Identificador único de la sala. */
  id: string;
  /** Deporte que se jugará en esta sala. */
  sport: Sport;
  /** Nombre visible de la sala. */
  name: string;
  /** Máximo de jugadores permitidos. */
  maxPlayers: number;
  /** Estado actual del ciclo de vida del lobby. */
  status: LobbyStatus;
  /** Lista de participantes conectados. */
  participants: LobbyParticipant[];
  /** ID del jugador anfitrión (creador). */
  hostId: string;
  /** Indica si la sala es privada (requiere código para entrar). */
  isPrivate: boolean;
  /** Código de acceso cuando la sala es privada. */
  accessCode: string | null;
  /** Configuración opcional del partido (duración, etc.). */
  settings: {
    matchDurationSec: number;
    allowSpectators: boolean;
    /** Béisbol: opciones elegidas por el anfitrión antes de empezar. */
    baseball?: BaseballOptions;
    /** Béisbol: alineación de cada equipo (sin ella, la automática). */
    lineups?: Partial<Record<'home' | 'away', TeamLineup>>;
  };
  /** Estadio seleccionado para esta partida (asset hiperrealista). */
  stadium: StadiumAsset | null;
  /** Condiciones ambientales / hora del día. */
  matchConditions: MatchConditions;
  /** Marca temporal de creación (ISO 8601). */
  createdAt: string;
}

// ─────────────────────────────────────────────
// Vistas reducidas para listas / catálogo
// ─────────────────────────────────────────────
export interface GameRoomSummary {
  id: string;
  sport: Sport;
  name: string;
  status: LobbyStatus;
  maxPlayers: number;
  currentPlayers: number;
  isPrivate: boolean;
  hostUsername: string;
  /** URL del thumbnail cinematográfico (preview de la sala). */
  thumbnailUrl: string | null;
  /** Estadio asignado (id + nombre para mostrar). */
  stadiumId: string | null;
  stadiumName: string | null;
  /** Likes / favoritos (como en Roblox). */
  likes: number;
  /** Total de visitas acumuladas. */
  visits: number;
  createdAt: string;
}

/** Categoría de "juego" para el catálogo del Hub (similar a "Experiences" en Roblox). */
export interface GameCatalogEntry {
  id: string;
  sport: Sport;
  title: string;
  description: string;
  /** Thumbnail cinematográfico (puede ser null hasta que el asset 3D real esté listo). */
  thumbnailUrl: string | null;
  /** Imagen panorámica del estadio. */
  heroUrl: string | null;
  /** Estadio que usa por defecto este juego. */
  stadiumId: string;
  /** Tags visibles (PvP, Cooperativo, Hardcore…). */
  tags: string[];
}

// ─────────────────────────────────────────────
// Conversión: GameRoom → GameRoomSummary
// ─────────────────────────────────────────────
export const toRoomSummary = (
  room: GameRoom,
  meta: { likes?: number; visits?: number; thumbnailUrl?: string | null } = {}
): GameRoomSummary => {
  const host = room.participants.find((p) => p.isHost);
  return {
    id: room.id,
    sport: room.sport,
    name: room.name,
    status: room.status,
    maxPlayers: room.maxPlayers,
    currentPlayers: room.participants.length,
    isPrivate: room.isPrivate,
    hostUsername: host ? host.player.username : 'desconocido',
    thumbnailUrl: meta.thumbnailUrl ?? null,
    stadiumId: room.stadium?.id ?? null,
    stadiumName: room.stadium?.name ?? null,
    likes: meta.likes ?? 0,
    visits: meta.visits ?? 0,
    createdAt: room.createdAt,
  };
};

// ─────────────────────────────────────────────
// Eventos del cliente → servidor (Socket.io)
// ─────────────────────────────────────────────
export interface ClientToServerEvents {
  'room:create': (
    payload: {
      sport: Sport;
      name: string;
      isPrivate: boolean;
      accessCode?: string;
      player: Player;
      /** Si hay una sala pública abierta del mismo deporte y nombre, unirse a ella en vez de crear otra. */
      matchmake?: boolean;
    },
    ack: (response: { ok: true; room: GameRoom } | { ok: false; error: string }) => void
  ) => void;
  'room:join': (
    payload: { roomId: string; player: Player; accessCode?: string },
    ack: (response: { ok: true; room: GameRoom } | { ok: false; error: string }) => void
  ) => void;
  'room:leave': (payload: { roomId: string }) => void;
  'lobby:switchTeam': (payload: { roomId: string; team: TeamSide }) => void;
  'lobby:toggleReady': (payload: { roomId: string }) => void;
  'lobby:kick': (payload: { roomId: string; targetPlayerId: string }) => void;
  /** Cambia las condiciones visuales de la sala (hora del día, clima, preset). */
  'lobby:setConditions': (payload: {
    roomId: string;
    timeOfDay?: TimeOfDay;
    weather?: WeatherCondition;
    lightingPresetId?: string;
  }) => void;
  /** Cambia el estadio asignado a la sala. */
  'lobby:setStadium': (payload: { roomId: string; stadiumId: string }) => void;
  /** Béisbol: el anfitrión cambia entradas, dificultad o misericordia (antes de empezar). */
  'lobby:setBaseballOptions': (payload: { roomId: string } & Partial<BaseballOptions>) => void;
  /**
   * Béisbol: alineación de un equipo (null = volver a la automática). Cada
   * equipo ordena la suya; el anfitrión ordena los equipos sin humanos. El
   * servidor la valida y responde con el motivo si la rechaza.
   */
  'lobby:setLineup': (
    payload: { roomId: string; team: 'home' | 'away'; lineup: TeamLineup | null },
    ack?: (res: { ok: true } | { ok: false; error: string }) => void
  ) => void;
  /**
   * Actualiza la identidad visible del jugador (nombre, color de camiseta,
   * dorsal). Todos los campos son opcionales: solo se aplica lo que llega.
   * El servidor valida, persiste en el participante y re-emite `room:state`.
   */
  'lobby:updateProfile': (payload: { roomId: string } & ProfilePatch) => void;
  /** Pide al servidor la lista actual de salas (al montar el Hub). */
  'rooms:refresh': () => void;

  // ─── Motor de partido ─────────────────────────────
  /** El host inicia el partido (transita lobby → match). */
  'match:start': (payload: { roomId: string }) => void;
  /**
   * Se (re)suscribe a un partido en curso: une el socket a la sala y
   * devuelve el estado actual. Necesario tras recargar `/match/:roomId`
   * o si la página del partido monta después de `match:started`.
   */
  'match:join': (
    payload: { roomId: string; playerId: string },
    ack: (
      response:
        | {
            ok: true;
            state: MatchState;
            /** El servidor devolvió al jugador su asiento original de la misma cuenta (sin duplicados ni sufijos). */
            as?: string;
          }
        | { ok: false; error: string; code?: 'inactive' | 'not-member' | 'no-match' }
    ) => void
  ) => void;
  /** Cliente → servidor: input del jugador en este frame. */
  'match:input': (payload: { roomId: string; input: PlayerInput }) => void;
  /** Cliente abandona el partido en curso. */
  'match:leave': (payload: { roomId: string }) => void;
  /** Pausa (solo con un humano): pestaña oculta o botón de pausa. */
  'match:pause': (payload: { roomId: string; reason: 'hidden' | 'manual' }) => void;
  /** Reanudar tras una pausa (cuenta atrás de 3 s) o recuperar el control si jugaba la CPU. */
  'match:resume': (payload: { roomId: string }) => void;
  /** Pestaña oculta / visible: oculta no recibe estados (ahorro de datos); al volver, uno completo. */
  'match:visibility': (payload: { roomId: string; hidden: boolean }) => void;
  /** Qué hacer al salir de la pantalla: pausar (solo con un humano) o que juegue la CPU. */
  'match:awayMode': (payload: { roomId: string; mode: AwayMode }) => void;
  /**
   * SOLO PRUEBAS (servidor con BASEBALL_TEST_HOOKS=1): coloca corredores CPU
   * en las bases indicadas y fuerza un batazo concreto del bateador actual.
   */
  'debug:baseballPlay': (payload: {
    roomId: string;
    bases: number[];
    /** Sin batazo: deja el turno listo para lanzar (robos, pickoffs). */
    hit?: { distance: number; angle: number; maxHeight: number };
    /** Retraso de reacción de los fildeadores CPU en esta jugada (s). */
    aiReaction?: number;
    /** Bases cuyos corredores intentan robar. */
    steal?: number[];
    /** Segundos hasta el lanzamiento de la CPU. */
    pitchIn?: number;
    /** Outs antes de la jugada. */
    outs?: number;
    /** Cambia la media entrada si hace falta para que batee el humano. */
    humanBats?: boolean;
  }) => void;
  /** SOLO PRUEBAS (BASEBALL_TEST_HOOKS=1): ajustes de la CPU en la sala. */
  'debug:baseballConfig': (payload: {
    roomId: string;
    aiSwing?: boolean;
    /** La CPU decide robos y pickoffs. */
    aiRun?: boolean;
    /** La CPU lanza siempre este tipo (null = normal). */
    forcePitch?: string | null;
    /** Azar en tiros y reacciones. */
    noise?: boolean;
    /** Punto lateral fijo de llegada de los lanzamientos (null = normal). */
    plateX?: number | null;
    /** La CPU lanzadora apunta siempre a esta zona (null = normal). */
    forceZone?: number | null;
    /** Resultado forzado del próximo toque de bola (null = normal). */
    buntOutcome?: 'fair' | 'foul' | 'miss' | null;
    /** La CPU se desliza siempre que puede. */
    forceSlide?: boolean;
    /** El próximo lanzamiento es descontrolado o bola pasada. */
    wild?: 'wild' | 'passed';
    /** El receptor intenta un pickoff tras el próximo lanzamiento. */
    catcherPickoff?: boolean;
    /** El próximo tiro del receptor sale desviado o se va al jardín. */
    catcherThrow?: 'offline' | 'error';
    /** Señas del receptor: siempre, nunca o normal (null). */
    signs?: 'always' | 'never' | null;
    /** Sin estadísticas (Fuerza/Velocidad/Inteligencia): escenarios de mecánica calibrados. */
    neutralStats?: boolean;
    noCover?: boolean;
    /** El próximo fildeador CPU comete un error. */
    forceError?: boolean;
    /** Resultado forzado del próximo contacto: foul tip, elevado de foul o pop-up al cuadro. */
    contact?: 'tip' | 'foulPop' | 'popup' | null;
    /** El próximo tercer strike no se atrapa: bloqueado cerca o se escapa lejos. */
    dropped?: 'near' | 'far' | null;
    /** La CPU da base intencional al próximo bateador (si puede). */
    ibb?: boolean;
    /** Reloj de lanzamiento activo (false = sin reloj, para pruebas largas). */
    clock?: boolean;
    /** El próximo batazo cerca de la barda bota por encima (doble por regla de terreno) o no. */
    groundRule?: boolean | null;
    /** Regla de los tres bateadores activa (false para pruebas de relevo que no la miden). */
    threeBatters?: boolean;
    /** El próximo tiro de un fildeador CPU se va desviado (error de tiro). */
    throwError?: boolean;
    /** Los corredores CPU se arriesgan siempre a la base extra (para provocar tiros). */
    aggressive?: boolean;
  }) => void;
}

// ─────────────────────────────────────────────
// Eventos del servidor → cliente (Socket.io)
// ─────────────────────────────────────────────
export interface ServerToClientEvents {
  'rooms:list': (rooms: GameRoomSummary[]) => void;
  'catalog:list': (entries: GameCatalogEntry[]) => void;
  'room:state': (room: GameRoom) => void;
  'room:closed': (payload: { roomId: string; reason: string }) => void;
  'lobby:playerJoined': (payload: { roomId: string; participant: LobbyParticipant }) => void;
  'lobby:playerLeft': (payload: { roomId: string; playerId: string }) => void;
  'lobby:conditionsChanged': (payload: {
    roomId: string;
    conditions: MatchConditions;
    stadium: StadiumAsset | null;
  }) => void;
  'lobby:error': (payload: { message: string }) => void;
  /**
   * El socket destinatario ya no está en la sala: expulsado por el host
   * (`kicked`) o desplazado porque el mismo jugador se unió desde otra
   * conexión (`duplicate`).
   */
  'lobby:kicked': (payload: { roomId: string; reason: 'kicked' | 'duplicate' | 'inactive' }) => void;

  // ─── Motor de partido ─────────────────────────────
  /** El servidor informa que el partido ha arrancado (cliente debe navegar a /match). */
  'match:started': (payload: { roomId: string; matchId: string; initialState: MatchState }) => void;
  /** Broadcast del estado del partido (alta frecuencia). */
  'match:state': (state: MatchState) => void;
  /** Gol / canasta / carrera detectado por el servidor. */
  'match:goal': (payload: {
    roomId: string;
    team: TeamSide;
    byPlayerId: string;
    score: { home: number; away: number };
    at: number;
    /** Puntos anotados (canasta de 2/3, carreras de un batazo). 1 por defecto. */
    points?: number;
  }) => void;
  /** Partido terminado (tiempo cumplido o forzado). */
  'match:ended': (payload: {
    roomId: string;
    finalScore: { home: number; away: number };
    reason: 'time' | 'manual' | 'left';
    /** Béisbol: marcador y estadísticas de bateo del partido. */
    summary?: BaseballSummary;
  }) => void;
  /** Elo/estadísticas de la cuenta actualizados tras un partido. */
  'profile:updated': (payload: {
    accountId: string;
    sport: Sport;
    elo: number;
    delta: number;
    outcome: 'win' | 'loss' | 'draw';
  }) => void;
}

// ─────────────────────────────────────────────
// Eventos disponibles en cada socket (helpers)
// ─────────────────────────────────────────────
export interface InterServerEvents {
  ping: () => void;
}

// ─────────────────────────────────────────────
// Datos asociados a cada conexión Socket.io
// ─────────────────────────────────────────────
export interface SocketData {
  playerId: string | null;
  currentRoomId: string | null;
  username: string | null;
  /** Id de la cuenta de Supabase validada con el token del handshake (null = invitado). */
  accountId: string | null;
}

// ─────────────────────────────────────────────
// Identidad: invitados vs cuentas
// ─────────────────────────────────────────────

/** Separador del "asiento" por pestaña de una cuenta: `<uuid>~<TAG>`. */
export const SEAT_SEPARATOR = '~';

/**
 * Ids sin cuenta: invitados locales (`guest_…`) y el modo local sin
 * Supabase (`mock_…`). Juegan, pero no puntúan Elo.
 */
export const isGuestId = (playerId: string): boolean =>
  playerId.startsWith('guest_') || playerId.startsWith('mock_');

/** Jugadores controlados por la CPU (los crea el servidor, nunca un cliente). */
export const isBotId = (playerId: string): boolean => playerId.startsWith('bot_');

/** Deportes que se pueden jugar contra la CPU con un solo humano. */
export const SOLO_SPORTS: readonly Sport[] = ['beisbol'];

/** Cuenta de Supabase detrás de un id de jugador (sin el asiento de pestaña). */
export const accountIdOf = (playerId: string): string => playerId.split(SEAT_SEPARATOR)[0] ?? playerId;

// ─────────────────────────────────────────────
// ============== MOTOR DE PARTIDO =============
// ─────────────────────────────────────────────

/** Vector 2D en el plano del campo. Usamos el mismo sistema que Three.js:
 *  +X hacia la portería "home" (lado derecho del campo).
 *  +Z hacia "atrás" de la portería home.
 *  El campo va de [-52.5, 52.5] en X y de [-34, 34] en Z. */
export interface Vec2 {
  x: number;
  z: number;
}

/** Fases del ciclo de vida de un partido. */
export type MatchPhase =
  | 'warmup'    // antes del kickoff
  | 'playing'   // cronómetro corriendo, balón en juego
  | 'paused'    // pausa (lesión, gol, etc.)
  | 'finished'; // tiempo cumplido o terminado manualmente

/** Personalización visual que cada jugador lleva al partido. */
export interface MatchAvatar {
  /** Color de la camiseta en HEX. */
  color: string;
  /** Dorsal (número) mostrado en la camiseta. */
  dorsal: number;
  /** Username para mostrar en la HUD. */
  username: string;
  /** País que representa (bandera en la HUD); ausente = color personalizado. */
  country?: string;
  /** Segundo color del uniforme (bandera): short, medias o mangas según el deporte. */
  color2?: string;
  /** Rostro elegido (código de `face.ts`); ausente = uno estable a partir del id (CPU incluidos). */
  face?: string;
}

/** Estado en tiempo real de un jugador dentro del partido. */
export interface PlayerState {
  playerId: string;
  team: TeamSide;
  position: Vec2;
  velocity: Vec2;
  /** `true` si está en el suelo (casi siempre). */
  onGround: boolean;
  avatar: MatchAvatar;
  /** Si tiene el control del balón en este momento (para UI). */
  hasBall: boolean;
  /** Cooldown hasta que puede volver a chutar (segundos). */
  kickCooldown: number;
}

/** Estado del balón. `height` se separa de la posición 2D para física
 *  vertical (saltos, rebotes). */
export interface BallState {
  position: Vec2;
  velocity: Vec2;
  height: number;
  heightVelocity: number;
  /** ID del jugador que tiene posesión, si lo hay. */
  ownerId: string | null;
}

/** Sub-fase de un turno de béisbol. */
export type BaseballPhase =
  | 'ready'   // el lanzador prepara el lanzamiento
  | 'windup'  // mecánica del lanzador (windup o set) hasta el soltado
  | 'pitch'   // la bola viaja hacia el home
  | 'inPlay'  // bola bateada en el aire / rodando: los fildeadores corren
  | 'result'  // pausa mostrando el resultado de la jugada
  | 'change'; // cambio de entrada: los equipos caminan a sus posiciones y al dugout

/**
 * Corredor en el diamante. `pos` es la posición continua sobre las bases:
 * 0 = home (bateador), 1 = primera, 2 = segunda, 3 = tercera, 4 = anotó.
 */
export interface BaseballRunner {
  playerId: string;
  /** Posición actual (0..4) sobre el recorrido home → 1ª → 2ª → 3ª → home. */
  pos: number;
  /** Base a la que se dirige (0..4). Se detiene al llegar. */
  target: number;
  /** Base desde la que salió en esta jugada. */
  from: number;
  /** Estaba obligado a avanzar (out forzado si la bola llega antes). */
  forced: boolean;
  /** Ventaja tomada en la base antes del lanzamiento (fracción del camino, visual). */
  lead?: number;
  /** Intento de robo: sale corriendo al soltar el lanzador la bola. */
  stealing?: boolean;
  /** Tras una atrapada al vuelo debe volver a pisar su base antes de avanzar (tag-up). */
  mustTag?: boolean;
  /** Deslizamiento hacia `base`: de pies, de cabeza o en gancho (lo decide el corredor; el servidor juzga). */
  slide?: { kind: SlideKind; base: number; tick: number } | null;
}

export type SlideKind = 'feet' | 'head' | 'hook';
/** Distancia a la base (fracción del camino entre bases) desde la que se puede deslizar. */
export const SLIDE_RANGE: [number, number] = [0.02, 0.35];
/**
 * ¿Puede deslizarse ahora? Corre hacia delante, le falta poco para la base y
 * no está ya encima. En la llegada a 1ª del bateador solo vale de cabeza.
 */
export const slideOptions = (r: BaseballRunner): SlideKind[] => {
  if (r.slide || r.target <= r.pos + 1e-6) return [];
  const base = Math.ceil(r.pos - 1e-9);
  const gap = base - r.pos;
  if (base < 1 || base > 4 || r.target < base || gap < SLIDE_RANGE[0] || gap > SLIDE_RANGE[1]) return [];
  if (base === 1 && r.from === 0) return ['head'];
  return ['feet', 'head', 'hook'];
};

/**
 * Órdenes puntuales de béisbol (el servidor valida y aplica):
 * corredores (avanzar, regresar, quedarse, cambiar de corredor, robar),
 * lanzador (tirar a 1ª/2ª/3ª), defensor con la bola (tirar a 1ª/2ª/3ª/home)
 * y saltar una transición.
 */
export const BASEBALL_CMDS = [
  'advance',
  'retreat',
  'stay',
  'next',
  'steal',
  'pickoff1',
  'pickoff2',
  'pickoff3',
  'throw1',
  'throw2',
  'throw3',
  'throw4',
  'skip',
  'relief',
  'bunt',
  'buntFirst',
  'buntThird',
  'boxUp',
  'boxBack',
  'boxIn',
  'boxOut',
  'timeOut',
  'switchSide',
  'routine',
  'slideFeet',
  'slideHead',
  'slideHook',
  'intentionalWalk',
] as const;
export type BaseballCmd = (typeof BASEBALL_CMDS)[number];
export const isBaseballCmd = (v: unknown): v is BaseballCmd =>
  typeof v === 'string' && (BASEBALL_CMDS as readonly string[]).includes(v);

export type BatterRoutine = 'tap' | 'twirl' | 'stretch' | 'relax';
export const BATTER_ROUTINES: BatterRoutine[] = ['tap', 'twirl', 'stretch', 'relax'];

/** Estado específico del béisbol (arcade: lanzar, batear, atrapar, correr). */
/** Resultado de una pulsación de «Batear» (ver `BaseballState.swingFx`). */
export type SwingResult = 'contact' | 'miss' | 'early' | 'late' | 'wait' | 'buntLate' | 'boxLocked' | 'outOfBox';

export interface BaseballState {
  inning: number;
  /** Total de entradas del partido. */
  innings: number;
  /** `top`: batea el visitante; `bottom`: batea el local. */
  half: 'top' | 'bottom';
  battingTeam: 'home' | 'away';
  outs: number;
  strikes: number;
  balls: number;
  /** Corredores en 1ª, 2ª y 3ª (derivado de `runners`, para la HUD). */
  bases: [boolean, boolean, boolean];
  /** Corredores en el diamante (jugadores reales del equipo que batea). */
  runners: BaseballRunner[];
  /** Extra innings: corredor automático colocado en 2ª al empezar esta media entrada. */
  autoRunner?: string | null;
  /** Reloj de lanzamiento (s que le quedan al lanzador humano); null si no corre. */
  pitchClock?: number | null;
  /** Corredor que controla cada humano del equipo que batea (id humano → id corredor). */
  control: Record<string, string>;
  /** Con la bola en la mano de un humano: base más útil para lanzar (resaltada). */
  suggestedThrow: number | null;
  /**
   * Base que debe cubrir cada humano que defiende en esta jugada (p. ej. el
   * lanzador en un rodado al lado de 1ª). La CPU nunca lo mueve por él: la HUD
   * se lo indica y otro de la CPU cubre también, así nunca se tira a una base vacía.
   */
  coverHint?: Record<string, number> | null;
  /** Tiro del lanzador a una base en curso (pickoff). */
  pickoffBase: number | null;
  /** Pickoffs fallidos en este turno al bate (tras 2, otro fallido es balk). */
  pickoffs?: number;
  /** Cambio de entrada: humanos que pulsaron "Omitir" y cuántos hacen falta. */
  skip?: { votes: number; needed: number } | null;
  /** Dificultad de la CPU y regla de misericordia de este partido. */
  difficulty?: BaseballDifficulty;
  mercy?: boolean;
  /** Posición defensiva de cada fildeador (P, C, 1B, SS, 3B, 2B, CF, LF, RF). */
  positions?: Record<string, string>;
  /** Orden al bate del equipo que batea e índice del bateador actual. */
  order?: string[];
  orderIndex?: number;
  /** Alineación de cada equipo para la transmisión: [id, posición] en el orden al bate. */
  lineups?: Record<'home' | 'away', Array<[string, string]>>;
  /** Bateo en vivo por jugador: [VB, H, C, K]. */
  box?: Record<string, [number, number, number, number]>;
  /** Lanzamientos del lanzador actual (la fatiga empieza a los 30). */
  pitchCount?: number;
  /** Relevista de cada equipo (null: lanza el titular). */
  relief?: { home: string | null; away: string | null };
  /**
   * Mecánica del lanzador en curso: windup (bases vacías) o set/stretch (con
   * corredores). El soltado ocurre exactamente en `releaseTick` (el servidor
   * manda); el cliente ajusta la animación a ese instante.
   */
  windup?: { kind: 'windup' | 'stretch'; startTick: number; releaseTick: number } | null;
  /** Antes de lanzar: el bateador (u otro jugador clave) aún camina a su sitio. */
  waiting?: boolean;
  /** Siguiente bateador, esperando en el círculo de espera (on deck). */
  onDeckId?: string | null;
  /** Última decisión en una base durante la jugada ("¡Out forzado en 2ª!", "Safe en 1ª"), al instante. */
  call?: { text: string; out: boolean; base: number; tick: number; homer?: boolean } | null;
  /** Fildeador que tiene la bola en la mano (puede lanzarla a una base). */
  ballHolderId: string | null;
  /** Base (1..4) a la que va un lanzamiento en el aire, para la HUD. */
  throwToBase: number | null;
  /**
   * Último lanzamiento: tipo y velocidad (visibles mientras la bola viene).
   * La posición final en el home y si fue en zona se rellenan al llegar.
   */
  pitchInfo: { type: PitchType; kmh: number; plateX?: number; plateY?: number; inZone?: boolean; zone?: number } | null;
  /**
   * Zona objetivo del lanzamiento en curso (desde que empieza la mecánica).
   * El bateador solo la ve con la ayuda activada en Ajustes.
   */
  pitchTarget?: number | null;
  /**
   * Seña del receptor antes de este lanzamiento (no en todos): tipo sugerido (dedos:
   * 1 recta, 2 curva, 3 slider, 4 cambio, 5 sinker) y zona (guante). La CPU lanzadora la
   * sigue; para el humano es una sugerencia (se puede ocultar en Ajustes).
   */
  sign?: { type: PitchType; zone: number; tick: number } | null;
  /** Devolución de la bola al lanzador tras el lanzamiento o la jugada: quién la tira y cuándo (animación). */
  ballReturn?: { tick: number; by: string } | null;
  /**
   * Lanzamientos del turno al bate actual: zona real y resultado
   * (S strike cantado, W abanicó, B bola, F foul, X en juego, H golpeado).
   */
  paPitches?: Array<{ z: number; r: 'S' | 'W' | 'B' | 'F' | 'X' | 'H' }>;
  /** El bateador se cuadró para tocar la bola (bunt) en este lanzamiento. */
  bunting?: boolean;
  /** Dirección del toque elegida: 1 por la línea de 1ª, -1 por la de 3ª, 0 o ausente = sin dirigir. */
  buntDir?: -1 | 0 | 1;
  /** Lado del bateador: 1 diestro (a la izquierda del home, x < 0), -1 zurdo. */
  batterSide?: 1 | -1;
  /**
   * Humano que controla al bateador de turno: él mismo si batea un humano, o
   * el humano de su equipo cuando batea un jugador de la CPU (alineación de 9
   * con bateador designado). null: batea la CPU.
   */
  batterCtl?: string | null;
  /** Mano del lanzador actual: R derecho, L zurdo (el modelo se dibuja en espejo). */
  pitcherHand?: 'R' | 'L';
  /** Fuerza, Velocidad e Inteligencia de cada jugador (fijas durante el partido). */
  attrs?: Record<string, PlayerAttrs>;
  /**
   * Posición del bateador en la caja: `depth` +1 adelante (hacia el lanzador)
   * … -1 atrás; `reach` +1 pegado al plato … -1 alejado (pasos de 0,5).
   */
  batterBox?: { depth: number; reach: number };
  /** «Tiempo» pedidos en este turno al bate (máximo 2) y segundos que quedan del actual. */
  timeOuts?: number;
  timeOut?: number;
  /** Preparación del bateador humano al empezar su turno (s que quedan; 0 = listo). */
  prep?: number;
  /**
   * Bullpen de cada equipo: lanzador de relevo (null si ya entró) y receptor
   * de bullpen. `tick`: último lanzamiento de calentamiento (solo el equipo
   * que defiende calienta).
   */
  bullpen?: Record<'home' | 'away', { p: string | null; c: string; tick: number | null; n?: number }>;
  /** Última rutina del bateador (animación): golpear los zapatos, girar el bate, estirar, relajarse. */
  routine?: { kind: BatterRoutine; tick: number } | null;
  /** Carreras por entrada de cada equipo (línea de anotación); índice 0 = 1ª entrada. */
  byInning?: { home: number[]; away: number[] };
  /** Marcador por equipo: carreras y hits. */
  line: { home: { r: number; h: number; e?: number }; away: { r: number; h: number; e?: number } };
  /** Texto de la última jugada terminada (se mantiene hasta la siguiente). */
  lastPlay: string | null;
  batterId: string | null;
  pitcherId: string | null;
  /** Receptor (catcher) del equipo que defiende: agachado detrás del home. */
  catcherId: string | null;
  phase: BaseballPhase;
  /** Segundos que quedan en la sub-fase actual (auto-lanzamiento, pausa…). */
  phaseTimer: number;
  /** Tick del último swing (animación del bate). */
  swingTick: number | null;
  /**
   * Qué pasó con la última pulsación de «Batear» del bateador humano (la HUD
   * lo explica; nunca falla en silencio): contacto, abanicó, muy pronto, muy
   * tarde o «aún no» (antes de que suelte la bola). También avisa de órdenes que
   * no cuentan: toque demasiado tarde, caja/tiempo fuera de plazo y batear fuera de la caja.
   */
  swingFx?: { tick: number; res: SwingResult; by?: string } | null;
  /** Punto de caída previsto de la bola bateada (marcador para fildeadores). */
  landing: Vec2 | null;
  /** Texto de la última jugada ("¡Strike!", "¡Jonrón!"…). */
  message: string | null;
}

/** Estado global de un partido. Se serializa y se envía ~20 veces/seg. */
export interface MatchState {
  /** Segundos que faltan para reanudar tras un gol (solo en fase `paused`). */
  resumeIn?: number;
  /**
   * Pausa de un partido de un solo humano (pestaña oculta, ausencia o botón).
   * Mientras dura no se simula nada. `resumeIn`: cuenta atrás al reanudar.
   */
  hold?: { reason: 'hidden' | 'away' | 'manual'; resumeIn: number | null } | null;
  /** Salas con varios humanos: ausentes por los que juega la CPU. */
  cpuFor?: string[];
  id: string;                  // normalmente = roomId
  roomId: string;
  /** Deporte del partido (define reglas, cancha y escena). */
  sport: Sport;
  /** Solo en béisbol. */
  baseball?: BaseballState;
  phase: MatchPhase;
  /** Segundos transcurridos desde el kickoff. */
  clock: number;
  /** Duración total del partido en segundos. */
  durationSec: number;
  score: { home: number; away: number };
  players: Record<string, PlayerState>;
  ball: BallState;
  /** Último gol / canasta / carrera (para animaciones / mensajes). */
  lastGoal: { team: TeamSide; at: number; byPlayerId: string; points?: number } | null;
  /** Contador incremental para depuración / sincronización. */
  tick: number;
}

/** Campos editables de la identidad visible de un jugador. */
export interface ProfilePatch {
  username?: string;
  color?: string;
  dorsal?: number;
  /** Código de país, o `null` para volver al color personalizado. */
  country?: string | null;
  /** Rostro (código de `face.ts`), o `null` para volver al automático. */
  face?: string | null;
}

/** Límites de validación de perfil (compartidos cliente/servidor). */
export const PROFILE_LIMITS = {
  usernameMin: 2,
  usernameMax: 20,
  dorsalMin: 1,
  dorsalMax: 99,
} as const;

/** Frecuencia del bucle autoritativo del servidor (Hz). */
export const MATCH_TICK_HZ = 20;
/** Duración de un tick en milisegundos. */
export const MATCH_TICK_MS = 1000 / MATCH_TICK_HZ;

/**
 * Input del cliente al servidor en un instante.
 * Las direcciones están en **espacio de mundo** (ejes del campo), ya
 * transformadas por el cliente según la cámara.
 */
export interface PlayerInput {
  /** Dirección X en el mundo: -1 ↔ +1 (hacia la portería visitante). */
  dirX: number;
  /** Dirección Z en el mundo: -1 ↔ +1. */
  dirZ: number;
  /** `true` si el jugador pidió chutar desde el último input enviado. */
  shoot: boolean;
  /** Béisbol: el corredor pide quedarse en la base (Shift / botón "Quedarse"). */
  hold?: boolean;
  /** Béisbol: tipo de lanzamiento elegido por el lanzador (teclas 1–5). */
  pitchType?: PitchType;
  /** Béisbol: zona objetivo elegida por el lanzador (0..16, ver `pitchZones.ts`). */
  pitchZone?: number;
  /** Béisbol: mano con la que lanza este jugador (preferencia de Ajustes). */
  throwHand?: 'R' | 'L';
  /** Béisbol: orden puntual (ver `BASEBALL_CMDS`). */
  cmd?: BaseballCmd;
  /**
   * Béisbol: progreso del lanzamiento (0..1) que el jugador veía en pantalla
   * al pulsar batear. Permite compensar la latencia de red.
   */
  swingProgress?: number;
  /**
   * Béisbol: progreso del lanzamiento (0..1) que el bateador VE ahora mismo
   * (se envía con cada input durante el lanzamiento). El servidor no resuelve
   * el toque ni cierra el lanzamiento hasta que su pantalla vio llegar la bola:
   * así un «Batear» pulsado antes nunca llega tarde (los mensajes llegan en orden).
   */
  viewProgress?: number;
  /** Timestamp del cliente (para reconciliación). */
  clientTs: number;
}
// ─────────────────────────────────────────────
// Validación de perfil (misma regla en cliente y servidor)
// ─────────────────────────────────────────────

/** Normaliza un nombre de usuario. Devuelve `null` si no es válido. */
export const sanitizeUsername = (raw: unknown): string | null => {
  if (typeof raw !== 'string') return null;
  // Quitamos caracteres de control y colapsamos espacios.
  const clean = raw.replace(/[\u0000-\u001f\u007f]/g, '').replace(/\s+/g, ' ').trim();
  if (clean.length < PROFILE_LIMITS.usernameMin) return null;
  return clean.slice(0, PROFILE_LIMITS.usernameMax);
};

/** Valida un color HEX `#rrggbb`. Devuelve `null` si no es válido. */
export const sanitizeColor = (raw: unknown): string | null =>
  typeof raw === 'string' && /^#[0-9a-fA-F]{6}$/.test(raw) ? raw.toLowerCase() : null;

/** Normaliza un dorsal al rango permitido. Devuelve `null` si no es numérico. */
export const sanitizeDorsal = (raw: unknown): number | null => {
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) return null;
  return Math.max(PROFILE_LIMITS.dorsalMin, Math.min(PROFILE_LIMITS.dorsalMax, Math.floor(n)));
};

// ─────────────────────────────────────────────
// Geometría de cada deporte (metros, compartida servidor/cliente)
// ─────────────────────────────────────────────

/**
 * Cancha de básquet FIBA: 28 × 15 m. El local ataca el aro de +X.
 * Los aros están a 1.575 m de la línea de fondo, a 3.05 m de altura.
 */
export const BASKETBALL = {
  halfX: 14,
  halfZ: 7.5,
  hoopX: 14 - 1.575,
  hoopHeight: 3.05,
  rimRadius: 0.23,
  threePointRadius: 6.75,
  ballRadius: 0.12,
} as const;

/**
 * Diamante de béisbol con el home en el origen y el campo hacia -Z
 * (la cámara mira hacia -Z). Bases a 27.43 m; montículo a 18.44 m.
 */
export const BASEBALL = {
  baseDistance: 27.43,
  moundZ: -18.44,
  /** Distancia a la valla del jardín (jonrón si la bola la supera). */
  fenceDistance: 95,
  ballRadius: 0.075,
} as const;

/** Nombre corto de una base (1..4) para mensajes. */
export const baseName = (base: number): string => ['home', '1ª', '2ª', '3ª', 'home'][base] ?? `${base}`;

/** Posiciones de 1ª, 2ª y 3ª base (home en el origen). */
export const baseballBasePositions = (): [Vec2, Vec2, Vec2] => {
  const d = BASEBALL.baseDistance / Math.SQRT2;
  return [
    { x: d, z: -d },
    { x: 0, z: -2 * d },
    { x: -d, z: -d },
  ];
};

// ─────────────────────────────────────────────
// Béisbol: tipos de lanzamiento
// ─────────────────────────────────────────────

export type PitchType = 'fastball' | 'curveball' | 'slider' | 'changeup' | 'sinker';

export interface PitchSpec {
  label: string;
  /** Tecla (1–5) para elegirlo. */
  key: string;
  icon: string;
  /** Velocidad mostrada en la HUD (km/h, mín–máx). */
  kmh: [number, number];
  /** Segundos del montículo al home en el juego (más lento que la vida real para poder batear). */
  duration: number;
  /** Quiebre lateral al final (m, + hacia primera base vista desde home). */
  breakX: number;
  /** Caída extra al final (m, negativa = cae). */
  dropY: number;
  /** Altura de la parábola (m): las curvas hacen más "loop". */
  arc: number;
  /** Dispersión del punto de llegada (m): control del lanzamiento. */
  spread: number;
  /** Ventana de swing total y perfecta (s) alrededor de la llegada. */
  swingWindow: number;
  perfectWindow: number;
  /** Multiplica la calidad del contacto. */
  contact: number;
  /** Desplaza la calidad hacia rodados (sinker). */
  grounderBias: number;
  /** Bateador CPU: sesgo y dispersión del timing (s). */
  aiBias: number;
  aiSigma: number;
  description: string;
}

/**
 * Lanzamientos. Justificación de los números en docs/LANZAMIENTOS.md.
 * Orden = teclas 1–5.
 */
export const PITCHES: Record<PitchType, PitchSpec> = {
  fastball: {
    label: 'Recta', key: '1', icon: '🔥', kmh: [146, 156], duration: 0.78,
    breakX: 0, dropY: 0, arc: 0.25, spread: 0.1,
    swingWindow: 0.2, perfectWindow: 0.06, contact: 1, grounderBias: 0,
    aiBias: 0, aiSigma: 0.1,
    description: 'La más rápida y recta: fácil de leer, pero da poco tiempo.',
  },
  curveball: {
    label: 'Curva', key: '2', icon: '🌀', kmh: [112, 122], duration: 1.02,
    breakX: -0.25, dropY: -0.55, arc: 0.55, spread: 0.18,
    swingWindow: 0.16, perfectWindow: 0.05, contact: 0.95, grounderBias: 0.05,
    aiBias: 0.03, aiSigma: 0.15,
    description: 'Lenta, sube y cae mucho al final. Engaña, pero se escapa de la zona con facilidad.',
  },
  slider: {
    label: 'Slider', key: '3', icon: '↪️', kmh: [128, 138], duration: 0.9,
    breakX: -0.45, dropY: -0.12, arc: 0.3, spread: 0.16,
    swingWindow: 0.17, perfectWindow: 0.05, contact: 0.95, grounderBias: 0.05,
    aiBias: 0.02, aiSigma: 0.14,
    description: 'Rápida con un quiebre lateral tardío. Arriesga bola si quiebra de más.',
  },
  changeup: {
    label: 'Cambio', key: '4', icon: '🐢', kmh: [125, 134], duration: 1.08,
    breakX: 0.12, dropY: -0.25, arc: 0.32, spread: 0.13,
    swingWindow: 0.2, perfectWindow: 0.06, contact: 0.9, grounderBias: 0.05,
    aiBias: -0.08, aiSigma: 0.14,
    description: 'Parece una recta pero llega más tarde: descuadra el timing del swing.',
  },
  sinker: {
    label: 'Sinker', key: '5', icon: '⤵️', kmh: [140, 148], duration: 0.84,
    breakX: 0.18, dropY: -0.3, arc: 0.25, spread: 0.12,
    swingWindow: 0.18, perfectWindow: 0.055, contact: 1, grounderBias: 0.15,
    aiBias: 0, aiSigma: 0.11,
    description: 'Rápida y cae al final: provoca rodados (buena para doble play).',
  },
};

export const PITCH_ORDER: PitchType[] = ['fastball', 'curveball', 'slider', 'changeup', 'sinker'];

export const isPitchType = (v: unknown): v is PitchType => typeof v === 'string' && v in PITCHES;

/** Zona de strike en el home (m): ancho y alturas. */
export const STRIKE_ZONE = { halfWidth: 0.4, low: 0.45, high: 1.15 } as const;

export interface BaseballSummary {
  line: { home: { r: number; h: number; e?: number }; away: { r: number; h: number; e?: number } };
  innings: number;
  batting: BattingLine[];
  /** Carreras por entrada (línea de anotación). */
  byInning?: { home: number[]; away: number[] };
  /** Por equipo (el que lanzaba): ponches conseguidos y lanzamiento más rápido (km/h). */
  pitching?: { home: { k: number; maxKmh: number }; away: { k: number; maxKmh: number } };
  /** Por equipo (el que corría): bases robadas y robos atrapados por el receptor rival. */
  running?: { home: { sb: number; cs: number }; away: { sb: number; cs: number } };
  /** Línea de cada lanzador (en orden de aparición) con su decisión. */
  pitchers?: PitchingLine[];
  /** Registro jugada por jugada. */
  log?: PlayLogEntry[];
  /** Jugador del partido. */
  mvp?: PlayerOfGame | null;
  /** El local ganó en la parte baja de la última entrada (o en extra innings). */
  walkOff?: boolean;
}

/** km/h → mph (millas por hora), redondeado. */
export const kmhToMph = (kmh: number): number => Math.round(kmh * 0.621371);

// ─────────────────────────────────────────────
// Béisbol: dugouts y círculo de espera (servidor y escena usan lo mismo)
// ─────────────────────────────────────────────

/**
 * Dugout de cada equipo, en territorio foul junto a la línea de 1ª (local)
 * o de 3ª (visitante). `along` apunta por la línea hacia la base y `out`
 * hacia fuera del campo (el fondo del dugout).
 */
export const baseballDugout = (team: 'home' | 'away') => {
  const side = team === 'home' ? 1 : -1;
  const along = { x: side * Math.SQRT1_2, z: -Math.SQRT1_2 };
  const out = { x: side * Math.SQRT1_2, z: Math.SQRT1_2 };
  // En territorio foul, junto a las gradas (empiezan a 22 m de la línea) y fuera de la tierra
  // del cuadro (círculo de 28 m alrededor del centro del diamante): antes estaba a 12 m, sobre la tierra.
  const center = { x: along.x * 10 + out.x * DUGOUT_OUT, z: along.z * 10 + out.z * DUGOUT_OUT };
  return { center, along, out, length: 11, depth: 2.6 };
};

/**
 * Sitio de cada jugador en su dugout: delante de la banca (el cliente lo
 * sienta en ella, `DUGOUT_SEAT_BACK` más atrás), repartidos a lo largo.
 */
export const baseballDugoutSpot = (team: 'home' | 'away', slot: number): Vec2 => {
  const d = baseballDugout(team);
  const k = ((slot % 10) - 4.5) * 0.85;
  return { x: d.center.x + d.along.x * k + d.out.x * DUGOUT_STAND_OUT, z: d.center.z + d.along.z * k + d.out.z * DUGOUT_STAND_OUT };
};
/** Distancia (m) del centro del dugout a su línea de foul. */
export const DUGOUT_OUT = 17.5;
/** Cajas de los coaches de 1ª y 3ª (en territorio foul, junto a cada base). */
export const baseballCoachBoxes = (): { first: Vec2; third: Vec2 } => {
  const h = baseballDugout('home');
  const a = baseballDugout('away');
  return {
    first: { x: h.along.x * 23 + h.out.x * 4.5, z: h.along.z * 23 + h.out.z * 4.5 },
    third: { x: a.along.x * 23 + a.out.x * 4.5, z: a.along.z * 23 + a.out.z * 4.5 },
  };
};
/** Dónde espera de pie cada jugador (m hacia el fondo del dugout desde su centro) y cuánto más atrás se sienta. */
export const DUGOUT_STAND_OUT = 1.2;
export const DUGOUT_SEAT_BACK = 0.62;

/**
 * Bullpen de cada equipo como uno real, en territorio foul a lo largo de su
 * línea (1ª el local, 3ª el visitante), entre su dugout y la valla del
 * jardín: dos zonas de lanzamiento paralelas (lomita con su goma y un home a
 * 18,44 m), una banca detrás para los relevistas y el sitio del entrenador.
 * El relevista que calienta usa la zona A y lanza hacia el home (hacia el
 * home del campo). Todo en metros: `along` por la línea, `out` hacia fuera.
 */
export const BULLPEN_PLATE_ALONG = 32;
export const BULLPEN_MOUND_DIST = 18.44;
export const BULLPEN_LANES_OUT = [8, 11.5] as const;
export const BULLPEN_BENCH = { out: 15.6, from: 35, to: 44 } as const;
export const baseballBullpenLayout = (team: 'home' | 'away') => {
  const d = baseballDugout(team);
  const at = (along: number, out: number): Vec2 => ({ x: d.along.x * along + d.out.x * out, z: d.along.z * along + d.out.z * out });
  const lanes = BULLPEN_LANES_OUT.map((o) => ({ plate: at(BULLPEN_PLATE_ALONG, o), rubber: at(BULLPEN_PLATE_ALONG + BULLPEN_MOUND_DIST, o) }));
  // Asientos de la banca (los relevistas que no calientan) y el entrenador, de pie junto a ella.
  const seats = [0, 1, 2, 3].map((k) => at(BULLPEN_BENCH.from + 1.2 + k * 2.1, BULLPEN_BENCH.out - 0.45));
  return {
    along: d.along,
    out: d.out,
    at,
    lanes,
    bench: { from: at(BULLPEN_BENCH.from, BULLPEN_BENCH.out), to: at(BULLPEN_BENCH.to, BULLPEN_BENCH.out) },
    seats,
    coach: at(BULLPEN_BENCH.from - 1.6, BULLPEN_BENCH.out - 1.2),
    /** Centro de todo el bullpen (cámara «Bullpen»). */
    center: at(BULLPEN_PLATE_ALONG + BULLPEN_MOUND_DIST / 2, (BULLPEN_LANES_OUT[0] + BULLPEN_BENCH.out) / 2),
  };
};
/** Relevista (en la goma de la zona A) y su receptor (agachado detrás del home de la zona A). */
export const baseballBullpen = (team: 'home' | 'away'): { pitcher: Vec2; catcher: Vec2 } => {
  const L = baseballBullpenLayout(team);
  return { pitcher: L.lanes[0]!.rubber, catcher: L.at(BULLPEN_PLATE_ALONG - 0.7, BULLPEN_LANES_OUT[0]) };
};
/** Jugadores del bullpen (no están en el campo ni en la alineación hasta que entran). */
export const isBullpenId = (id: string): boolean => /^bot_(home|away)_b[pc]$/.test(id);
/** Bateador designado de cada equipo (batea en lugar del lanzador; no defiende). */
export const isDhId = (id: string): boolean => /^bot_(home|away)_dh$/.test(id);

/** Círculo de espera (on deck) de cada equipo, entre su dugout y el home. */
export const baseballOnDeck = (team: 'home' | 'away'): Vec2 => {
  const d = baseballDugout(team);
  return { x: d.along.x * 3 + d.out.x * 6, z: d.along.z * 3 + d.out.z * 6 };
};

// ─────────────────────────────────────────────
// Béisbol: opciones del partido (lobby)
// ─────────────────────────────────────────────
export type BaseballDifficulty = 'facil' | 'normal' | 'dificil';
export interface BaseballOptions {
  innings: 1 | 3 | 5 | 9;
  difficulty: BaseballDifficulty;
  /** Regla de misericordia: máximo 8 carreras por media entrada. */
  mercy: boolean;
}
export const BASEBALL_INNINGS_OPTIONS = [1, 3, 5, 9] as const;
// Regla estándar: la media entrada termina con 3 outs. La misericordia (8 carreras) es opcional y va desactivada.
export const BASEBALL_DEFAULT_OPTIONS: BaseballOptions = { innings: 3, difficulty: 'normal', mercy: false };
export const BASEBALL_DIFFICULTY_LABEL: Record<BaseballDifficulty, string> = {
  facil: 'Fácil',
  normal: 'Normal',
  dificil: 'Difícil',
};
/** Valida opciones recibidas (lo desconocido se ignora y queda lo anterior). */
export const sanitizeBaseballOptions = (raw: Partial<BaseballOptions> | undefined, base = BASEBALL_DEFAULT_OPTIONS): BaseballOptions => ({
  innings: (BASEBALL_INNINGS_OPTIONS as readonly number[]).includes(raw?.innings as number) ? (raw!.innings as BaseballOptions['innings']) : base.innings,
  difficulty: raw?.difficulty === 'facil' || raw?.difficulty === 'normal' || raw?.difficulty === 'dificil' ? raw.difficulty : base.difficulty,
  mercy: typeof raw?.mercy === 'boolean' ? raw.mercy : base.mercy,
});

/** Al salir de la pantalla durante un partido: pausarlo (un solo humano) o que juegue la CPU. */
export type AwayMode = 'pause' | 'cpu';

/**
 * Mecánica del lanzador (s): del inicio del movimiento al soltado. Corta e
 * idéntica para todos; ver docs/MECANICA_Y_UMPIRES.md.
 */
export const PITCH_MECHANICS = { windupSec: 1.4, stretchSec: 0.8 } as const;
/** Punto de soltado de la bola respecto al montículo (mano derecha, por encima del hombro). */
export const PITCH_RELEASE = { x: -0.35, y: 2.05 } as const;

export * from './countries';
export * from './face';
export * from './pitchZones';
export * from './lineup';
export * from './baseballStats';
import type { BattingLine, PitchingLine, PlayLogEntry, PlayerOfGame } from './baseballStats';
export * from './wire';
import type { PlayerAttrs, TeamLineup } from './lineup';
